import type { TodayWorkspace } from '@norum/contracts';

/** Busy intervals are hydrated across every page on the server. Never infer
 * availability from the currently displayed queue page. */
export function freeHours(day: TodayWorkspace['selectedDay'], busy: TodayWorkspace['busy'], zone: string, now: Date, hours: { start: number; end: number }) {
  const out: Array<{ at: string; label: string }> = [];
  const clock = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  for (let at = Date.parse(day.from); at < Date.parse(day.to); at += 3_600_000) {
    const end = Math.min(at + 3_600_000, Date.parse(day.to)), label = clock.format(new Date(at));
    const [hourPart, minutePart] = label.split(':').map(Number);
    const hour = hourPart! + minutePart! / 60;
    if (hour < hours.start || hour + 1 > hours.end || end - at < 3_600_000 || at < now.getTime()) continue;
    if (busy.some(item => Date.parse(item.from) < end && Date.parse(item.to) > at)) continue;
    out.push({ at: new Date(at).toISOString(), label });
  }
  const counts = new Map<string, number>();
  for (const slot of out) counts.set(slot.label, (counts.get(slot.label) ?? 0) + 1);
  const offset = new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'shortOffset' });
  return out.map(slot => counts.get(slot.label)! > 1 ? { ...slot, label: `${slot.label} ${offset.formatToParts(new Date(slot.at)).find(part => part.type === 'timeZoneName')?.value ?? zone}` } : slot);
}
export function FreeHours({ workspace, hours, open }: { workspace: TodayWorkspace; hours: { start: number; end: number }; open: (path: string) => void }) {
  if (!workspace.selectedDay || !workspace.busy) return null;
  const slots = freeHours(workspace.selectedDay, workspace.busy, workspace.zone, new Date(), hours);
  return <section aria-label="Free hours to plan" className="space-y-2">
    <h3 className="font-semibold">Free hours to plan</h3>
    <p>Calendar and placed-work gaps in your working hours. Unscheduled tasks do not reserve time; meeting booking checks your actual availability again.</p>
    {slots.length ? <ul className="flex flex-wrap gap-3">{slots.map(slot => <li key={slot.at}><button className="underline min-h-11 min-w-11 px-2" onClick={() => open(`/calendar?at=${encodeURIComponent(slot.at)}`)}>{slot.label} Free</button></li>)}</ul> : <p>No complete free hours remain in this day.</p>}
  </section>;
}
